import { unitLabel, warrantyUnitValue } from '@/lib/orders';

import type { OrderDevice } from './types';

/**
 * One option per eligible Unit, in response order.
 *
 * The response **is** the option list — there is no filtering here, deliberately (ADR-0024). A
 * Bundle is never returned as itself, only one row per component device sharing the Bundle's
 * `orderItemId`, so "the Bundle is not selectable" is already true with no frontend logic.
 *
 * `unitLabel` and `warrantyUnitValue` come from `@/lib/orders`, **not** `@/features/orders`: this
 * module is imported *by* `orders`, so reaching back would close a cycle whose known failure mode is
 * a value import resolving to `undefined` at module init (ADR-0024, ticket 01).
 */
export function toDeviceOptions(rows: OrderDevice[]): Array<{ value: string; label: string }> {
  return rows.map((row) => ({
    value: warrantyUnitValue(row.orderItemId, row.unitIndex, row.bundleDeviceId ?? null),
    label: unitLabel(row.productName, row.quantity, row.unitIndex),
  }));
}

/**
 * The Unit an Order Summary row link preselects for one line item, or `null` when that line item has
 * nothing eligible — so **one call answers both** "does the link render?" and "which Unit does it
 * open with?".
 *
 * Presence in the response is the *entire* rule. The backend applies all five predicates — order
 * status, cancellation, approved replacements, bundle inheritance, already-configured, DM-only — and
 * a line item it omits simply has no link (ADR-0024, FLAG-BE-1). Nothing here reads `itemType`, a
 * device status, or the order.
 *
 * "First" is **response order**, per the AC: `find` takes the earliest returned Unit, and the
 * dropdown still lists the rest so the admin can correct the choice.
 */
export function firstEligibleUnitValue(rows: OrderDevice[], orderItemId: string): string | null {
  const row = rows.find((r) => r.orderItemId === orderItemId);
  return row ? warrantyUnitValue(row.orderItemId, row.unitIndex, row.bundleDeviceId ?? null) : null;
}
